import { useEffect, useState } from 'react';
import { Outlet, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedCountry } from '../../hooks/useSelectedCountry';
import { userDisplayName } from '../../types';
import { ThemeToggle } from '../ui/ThemeToggle';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { CategoryIcon } from '../ui/CategoryIcon';
import { CountrySelectionDialog } from '../ui/CountrySelectionDialog';
import { Flag } from '../ui/Flag';
import { getFeaturedCategories, type CategorySlug } from '../../assets/categoryImages';
import { Footer } from './Footer';

export function PublicShell() {
  const { t } = useTranslation();
  const { isAuthenticated, user, hasRole } = useAuth();
  const navigate = useNavigate();

  // "Create a pro account" pill — entry point to the marketing/funnel
  // landing page, which then smart-routes the user based on auth state.
  const handleProClick = () => {
    if (isAuthenticated && hasRole('PROVIDER')) {
      navigate('/dashboard');
    } else {
      navigate('/pro');
    }
  };

  // Discover dropdown — categories
  const [discoverAnchor, setDiscoverAnchor] = useState<HTMLElement | null>(null);
  const discoverOpen = Boolean(discoverAnchor);
  const closeDiscover = () => setDiscoverAnchor(null);
  const handleCategoryPick = (slug: CategorySlug) => {
    closeDiscover();
    closeMobileMenu();
    navigate(`/category/${slug}`);
  };

  // Mobile navigation drawer — a full-height side panel (not a floating
  // popover) so Discover + auth actions are comfortably thumb-reachable
  // on phone widths without overlapping the page behind it.
  const [mobileOpen, setMobileOpen] = useState(false);
  function closeMobileMenu() {
    setMobileOpen(false);
  }

  // Country selection — pops up on the visitor's first arrival (when no
  // choice is yet stored in localStorage) and is re-openable via the flag
  // pill in the navbar.  The selection is consumed by the search bar's
  // default country and by any /api call that needs to know the market.
  const { countryCode, hasUserChosen, setCountryCode } = useSelectedCountry();
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);

  useEffect(() => {
    // First-time visitors get the chooser; returning visitors don't.
    if (!hasUserChosen) setCountryDialogOpen(true);
  }, [hasUserChosen]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <AppBar position="sticky" elevation={0}>
        <Toolbar
          sx={{
            gap: { xs: 0.5, sm: 1, md: 1.5 },
            px: { xs: 1.5, sm: 2, md: 3 },
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {/* Brand lockup — the full color logo PNG already contains the
              wordmark, so no separate <Typography> is needed. {@code alt}
              still announces the brand name for screen readers / SEO. */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src="/logo-color.png"
              alt={t('app.name')}
              loading="eager"
              sx={{
                display: 'block',
                // Horizontal wordmark (A + "Afro Espace" inline,
                // ~2.56:1 aspect ratio): height drives visual size,
                // maxWidth caps total footprint on tight mobile bars.
                height: { xs: 32, sm: 40 },
                width: 'auto',
                maxWidth: { xs: 130, sm: 170 },
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          </Box>

          <Button
            color="inherit"
            onClick={(e) => setDiscoverAnchor(e.currentTarget)}
            endIcon={
              <KeyboardArrowDownRoundedIcon
                sx={{
                  transition: 'transform 200ms ease',
                  transform: discoverOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            }
            aria-haspopup="true"
            aria-expanded={discoverOpen ? 'true' : undefined}
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {t('nav.discover')}
          </Button>
          <Menu
            anchorEl={discoverAnchor}
            open={discoverOpen}
            onClose={closeDiscover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 240,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '0 12px 32px rgba(0,0,0,0.55)'
                      : '0 12px 32px rgba(26,24,22,0.12)',
                },
              },
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: 'block',
                px: 2,
                pt: 1.5,
                pb: 0.5,
                letterSpacing: '0.12em',
                fontWeight: 700,
              }}
            >
              {t('nav.discoverDropdownTitle')}
            </Typography>
            {getFeaturedCategories(countryCode).map((cat) => (
              <MenuItem
                key={cat.slug}
                onClick={() => handleCategoryPick(cat.slug)}
                sx={{ py: 1.25, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: '32px !important', color: 'primary.main' }}>
                  <CategoryIcon code={cat.slug} fontSize="small" />
                </ListItemIcon>
                <Typography sx={{ fontWeight: 500 }}>{t(cat.i18nKey)}</Typography>
              </MenuItem>
            ))}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => {
                closeDiscover();
                navigate('/search');
              }}
              sx={{ py: 1.25, px: 2, color: 'text.secondary' }}
            >
              <Typography sx={{ flex: 1, fontWeight: 600 }}>
                {t('common.viewAll')}
              </Typography>
              <ArrowForwardRoundedIcon fontSize="small" />
            </MenuItem>
          </Menu>

          <Button
            component={RouterLink}
            to="/requests"
            color="inherit"
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {t('nav.requests')}
          </Button>

          <Button
            component={RouterLink}
            to="/opportunities"
            color="inherit"
            startIcon={<LightbulbOutlinedIcon />}
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {t('nav.opportunities')}
          </Button>

          <Button
            onClick={handleProClick}
            variant="outlined"
            size="small"
            startIcon={<StorefrontRoundedIcon />}
            sx={{
              borderRadius: 999,
              px: 2,
              py: 0.6,
              fontWeight: 600,
              color: 'text.primary',
              borderColor: 'divider',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: { xs: 'none', md: 'inline-flex' },
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {t('nav.createProAccount')}
          </Button>

          {isAuthenticated ? (
            <>
              <Button
                color="inherit"
                component={RouterLink}
                to="/dashboard"
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                {t('nav.dashboard')}
              </Button>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: { xs: 'none', md: 'inline' },
                }}
              >
                {user ? userDisplayName(user) : ''}
              </Typography>
            </>
          ) : (
            <>
              <Button
                color="inherit"
                onClick={() => navigate('/login')}
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                {t('auth.login')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                {t('auth.register')}
              </Button>
            </>
          )}

          {/* Country pill — shows the current market and re-opens the
              country-selection dialog when clicked.  Hidden on xs to keep
              the toolbar tight; the hamburger menu surfaces it instead. */}
          <Button
            color="inherit"
            onClick={() => setCountryDialogOpen(true)}
            aria-label={t('country.changeCountry')}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              minWidth: 0,
              px: 1.25,
              py: 0.4,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'divider',
              gap: 0.75,
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <Flag alpha2={countryCode} size="1.125rem" />
            <Typography
              component="span"
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: 'text.primary',
              }}
            >
              {countryCode}
            </Typography>
          </Button>

          <LanguageSwitcher />
          <ThemeToggle />

          {/* Mobile hamburger — visible only on xs.  Consolidates Discover
              + auth actions so the bar fits on phone widths without text
              wrapping. */}
          <IconButton
            color="inherit"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.menu')}
            aria-haspopup="true"
            aria-expanded={mobileOpen ? 'true' : undefined}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, flexShrink: 0 }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Drawer
            anchor="right"
            open={mobileOpen}
            onClose={closeMobileMenu}
            PaperProps={{
              sx: {
                width: '86vw',
                maxWidth: 360,
                display: 'flex',
                flexDirection: 'column',
                borderTopLeftRadius: 16,
                borderBottomLeftRadius: 16,
              },
            }}
          >
            {/* Drawer header — title + a large, always-visible close
                target so the panel never traps the user. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                minHeight: 56,
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '1.0625rem' }}>
                {t('nav.menu')}
              </Typography>
              <IconButton
                onClick={closeMobileMenu}
                aria-label={t('common.close')}
                edge="end"
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
            <Divider />

            {/* Scrollable body — keeps the register CTA pinned below
                even when the category list is long. */}
            <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
              <List disablePadding>
                {/* Change country — surfaced first so mobile visitors
                    can pick their market without hunting. */}
                <ListItemButton
                  onClick={() => {
                    closeMobileMenu();
                    setCountryDialogOpen(true);
                  }}
                  sx={{ py: 1.25, px: 2, minHeight: 52 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Flag alpha2={countryCode} size="1.125rem" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('country.changeCountry')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>

                {isAuthenticated ? (
                  <ListItemButton
                    onClick={() => {
                      closeMobileMenu();
                      navigate('/dashboard');
                    }}
                    sx={{ py: 1.25, px: 2, minHeight: 52 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                      <DashboardRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.dashboard')}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    onClick={() => {
                      closeMobileMenu();
                      navigate('/login');
                    }}
                    sx={{ py: 1.25, px: 2, minHeight: 52 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                      <LoginRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('auth.login')}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                )}
                <ListItemButton
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/opportunities');
                  }}
                  sx={{ py: 1.25, px: 2, minHeight: 52 }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    <LightbulbOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.opportunities')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => {
                    closeMobileMenu();
                    handleProClick();
                  }}
                  sx={{ py: 1.25, px: 2, minHeight: 52 }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    <StorefrontRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.createProAccount')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </List>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  display: 'block',
                  px: 2,
                  pb: 0.5,
                  letterSpacing: '0.12em',
                  fontWeight: 700,
                }}
              >
                {t('nav.discoverDropdownTitle')}
              </Typography>
              <List disablePadding>
                {getFeaturedCategories(countryCode).map((cat) => (
                  <ListItemButton
                    key={cat.slug}
                    onClick={() => handleCategoryPick(cat.slug)}
                    sx={{ py: 1.25, px: 2, minHeight: 52 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                      <CategoryIcon code={cat.slug} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(cat.i18nKey)}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
                <ListItemButton
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/search');
                  }}
                  sx={{ py: 1.25, px: 2, minHeight: 52, color: 'text.secondary' }}
                >
                  <ListItemText
                    primary={t('common.viewAll')}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ArrowForwardRoundedIcon fontSize="small" />
                </ListItemButton>
              </List>
            </Box>

            {/* Pinned register CTA — the primary action stays reachable
                without scrolling the whole panel. */}
            {!isAuthenticated && (
              <Box
                sx={{
                  p: 2,
                  flexShrink: 0,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/register');
                  }}
                  sx={{ borderRadius: 2, py: 1.1, fontWeight: 700 }}
                >
                  {t('auth.register')}
                </Button>
              </Box>
            )}
          </Drawer>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
        <Outlet />
      </Box>

      <Footer />

      <CountrySelectionDialog
        open={countryDialogOpen}
        currentCountry={countryCode}
        onClose={() => setCountryDialogOpen(false)}
        onConfirm={(alpha2) => setCountryCode(alpha2)}
      />
    </Box>
  );
}
